
import { Component, Vue, Watch } from "vue-property-decorator";
import vuetifyToast from "vuetify-toast";
import AppEvents from "@/AppEvents";
import TranslateView from "@/components/Translate/views/TranslateView.vue";
import "./AppRoot.scss";
import Routes from "@/Routes";
import { Destination } from "@/components/Passenger/models/Destination";
import { TimetablePage } from "@/pages/TimetablePage.vue";
import { ArrivalsDeparturesPage } from "@/pages/ArrivalsDeparturesPage.vue";
import { PeoplePage } from "@/pages/PeoplePage.vue";
import Version from "@/models/Version";
import { RouteConfig, RouterOptions } from "vue-router";
import ITheme from "./themes/ITheme";
import PassengerSearch from "./components/PassengerSearch.vue";
import OrderSearch from "./components/OrderSearch.vue";
import { Organization } from "./models/Organization";
import User from "@/models/User";
import { IPassengerSearchResult, ITimetableSearchResult } from "./typings/timit";
import { PermissionEnum } from "./models/DestinationRole";
import DestinationSelector from "./components/Passenger/components/DestinationSelector.vue"

enum ERouteType {
  Destination = 0,
  Organization = 1,
}

@Component({
  template: require("./AppRoot.vue.html"),
  components: {
    TranslateView,
    PassengerSearch,
    DestinationSelector,
    OrderSearch,
  },
})
export default class AppRoot extends Vue {
  drawer: any = null;
  isResourcesLoading: boolean = true;
  apiError: boolean = false;
  translatePanePreferredWidth: string = "50%";
  refreshing: boolean = false;
  pollApiInfoTimeout: any;
  newVersion: string = "";
  themeToggleEnabled: boolean = false;
  search: string = "";
  routeType = ERouteType;

  $vuetify: any;

  constructor() {
    super();
  }

  get themeId() {
    return this.$store.state.app.themeId;
  }

  @Watch("themeId", { immediate: true })
  onThemeIdChanged(val, oldVal) {
    // console.log("onThemeIdChanged");
    // console.log("this.theme.vuetifyTheme = ", this.theme.vuetifyTheme);
    // console.log("this.$vuetify.theme = ", this.$vuetify.theme);
    Object.assign(this.$vuetify.theme, this.theme.vuetifyTheme);
  }

  get theme(): ITheme {
    return this.$store.state.app.themes.find((t) => t.id === this.themeId);
  }

  toggleTheme() {
    const themeId =
      this.themeId === "default"
        ? "kovanen"
        : this.themeId === "kovanen"
        ? "espoo"
        : "default";
    this.$store.dispatch("app/setThemeId", themeId);
  }

  get themeCssClassName() {
    return this.theme.cssClassName;
  }

  get isLoading() {
    // return true;
    return this.isResourcesLoading || this.isLocalesLoading;
  }

  get hasPermissionToTimeTables() {
    if (this.me) {
      return this.me.destinationRoles.some(r => r.timetablePermission >= PermissionEnum.Read);
    }
    return false;
  }

  get isAuthenticated() {
    return this.$store.getters["auth/isAuthenticated"];
  }
  get isLocalesLoading() {
    return !this.$store.state.app.localesLoaded;
  }
  get me(): User {
    return this.$store.state.user.me;
  }
  get showWeekends() {
    return this.$store.state.user.showWeekends;
  }
  set showWeekends(val) {
    // no-op
  }
  async onToggleShowWeekends() {
    this.$store.dispatch("user/setShowWeekends", !this.showWeekends);
  }
  get weekStartsOnSunday() {
    return this.$store.state.user.weekStartsOnSunday;
  }
  set weekStartsOnSunday(val) {
    // no-op
  }

  get navigationRoutes(): any[] {
    const topLevelRoutes = (this.$router as any).options.routes;
    const childRoutesArr: any[] = topLevelRoutes
      .map((r) => r.children)
      .filter((r) => r);
    const childRoutes = [].concat(...childRoutesArr);
    const allRoutes = topLevelRoutes.concat(childRoutes);
    const navRoutes = allRoutes.filter(
      (r) => r.meta && r.meta.nav && (r.meta.guest || this.isAuthenticated)
    );
    return navRoutes;
  }

  get allowedNavigationRoutes(): any[] {
    const routes = this.me
      ? this.navigationRoutes.filter(
          (r) =>
            !r.meta ||
            (r.meta &&
              r.meta.requireDestinationPermissions &&
              this.me.hasPermissions(
                this.routeDestinationId,
                r.meta.requireDestinationPermissions
              ))
        )
      : [];
    const a = routes.find(t => t.name === "Timetables");
    const b = routes.find(t => t.name === "SingleBooking");
    if (!!a && !!b) {
      const index = routes.indexOf(b);
      routes.splice(index, 1);
    }
    return routes;
  }

  get splitPaneRef(): Vue {
    return this.$refs.splitPane as Vue;
  }

  get contentPaneHtmlElement(): HTMLElement {
    return this.splitPaneRef && this.splitPaneRef.$children
      ? (this.splitPaneRef.$children[0].$el as HTMLElement)
      : null;
  }

  get resizeHandleHtmlElement(): HTMLElement {
    return this.splitPaneRef && this.splitPaneRef.$children
      ? (this.splitPaneRef.$children[1].$el as HTMLElement)
      : null;
  }

  get translatePaneHtmlElement(): HTMLElement {
    return this.splitPaneRef && this.splitPaneRef.$children
      ? (this.splitPaneRef.$children[2].$el as HTMLElement)
      : null;
  }

  get translateModeEnabled(): boolean {
    return document.location.hostname.indexOf("localhost") > -1;
  }

  get translateMode(): boolean {
    return this.$store.state.app.translateMode;
  }

  set translateMode(val) {
    this.$store.dispatch("app/setTranslateMode", val);
  }

  get storeTranslateMode(): boolean {
    return this.$store.state.app.translateMode;
  }

  get destinations(): Destination[] {
    return this.$store.state.destination.destinations;
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  get selectedDestinationId(): number {
    return this.routeDestinationId;
  }

  set selectedDestinationId(destinationId: number) {
    //  this.$router.replace({
    //         name: 'ArrivalsDeparturesDestination',
    //         params: {
    //         destinationId: firstDestinationId.toString(),
    //         }
    //     });
    this.$router.push({
      name: Routes.destinationRoute.name,
      params: { destinationId: destinationId.toString() },
    });
  }

  get selectedOrganization() {
    const selectedOrganization = this.organizations.find(
      (o) => o.id === this.organizationId
    );
    return selectedOrganization ? selectedOrganization : null;
  }

  get selectedDestination() {
    const selectedDestination = this.destinations.find(
      (d) => d.id === this.selectedDestinationId
    );
    return selectedDestination ? selectedDestination : null;
  }

  get extendedToolbarTitle(): string {
    if (this.currentRouteType === ERouteType.Organization) {
      return this.selectedOrganization ? this.selectedOrganization.name : "";
    }
    return this.selectedDestination ? this.selectedDestination.alias : "";
  }

  get extendedToolbarCaption(): string {
    if (this.currentRouteType === ERouteType.Organization) {
      return this.$t("route.organization", "Organization");
    }
    return this.selectedDestination
      ? this.selectedDestination.movitInstanceName
      : "";
  }

  get organizations(): Organization[] {
    return this.$store.state.organization.organizations;
  }

  get userIsOrganizationAdmin(): boolean {
    if (!this.organizationId) {
      return false;
    }
    return this.organizations.some(
      (o) => o.id === this.organizationId && o.administratorIds.some(id => id === this.me.id) 
    );
  }

  get organizationNavRoutes(): RouteConfig[] {
    const organizationRoot = (this.$router as any).options.routes.find(
      (r: any) => r.meta && r.meta.organizationRoot
    ) as RouteConfig;

    if (!organizationRoot) {
      return [];
    }

    let navRoutes = organizationRoot.children
      .map((c) => c)
      .filter((c) => c.meta && c.meta.organizationNav);

    // if admin -> show all
    // if not admin -> show only ones without "meta/requireAdmin"
    if (!this.userIsOrganizationAdmin) {
      navRoutes = navRoutes.filter((nr) => nr.meta.requireAdmin === false);
    }

    return navRoutes;
  }

  get currentRouteType() {
    if (this.$route.path.includes("/o/")) {
      return ERouteType.Organization;
    }
    return ERouteType.Destination;
  }

  get currentRouterPath() {
    return this.$router.currentRoute;
  }

  get organizationId() {
    return this.$store.state.route.params.organizationId || "";
  }

  @Watch("translateMode")
  onTranslateModeChanged(val, oldVal) {
    this.toggleTranslateMode(this.translateMode);
  }
  toggleTranslateMode(enabled) {
    this.drawer = false;
    if (
      !this.contentPaneHtmlElement ||
      !this.resizeHandleHtmlElement ||
      !this.translatePaneHtmlElement
    ) {
      return;
    }
    if (enabled) {
      const contentPanePreferredWidth: string =
        (100.0 - parseFloat(this.translatePanePreferredWidth)).toString() + "%";
      this.contentPaneHtmlElement.style.width = contentPanePreferredWidth;
      this.resizeHandleHtmlElement.style.left = contentPanePreferredWidth;
      this.translatePaneHtmlElement.style.width =
        this.translatePanePreferredWidth;
      this.resizeHandleHtmlElement.style.display = "block";
      this.translatePaneHtmlElement.style.display = "block";
    } else {
      this.contentPaneHtmlElement.style.width = "100%";
      this.resizeHandleHtmlElement.style.left = "100%";
      this.translatePaneHtmlElement.style.width = "0";
      this.resizeHandleHtmlElement.style.display = "none";
      this.translatePaneHtmlElement.style.display = "none";
    }
  }
  async getMe() {
    return this.$store.dispatch("user/getMe");
  }

  async getAllMovitInstanceConfigs() {
    return this.$store.dispatch("app/getAllMovitInstanceConfigs");
  }

  setLocale(locale) {
    this.$store.dispatch("app/setLocale", locale);
    this.drawer = false;
  }

  async initApp() {
    this.isResourcesLoading = true;
    if (this.isAuthenticated) {
      try {
        await this.getMe();
        await this.getAllMovitInstanceConfigs();
        await this.getAllDestinations();
        const organizations = await this.getAllOrganizations();
        if (organizations && organizations.data.length > 0) {
          for(let i = 0; i < organizations.data.length; i++) {
            await this.getOrganizationPtpNames(organizations.data[i].id)
            await this.getOrganizationCostCenters(organizations.data[i].id)
          }
        }
      } catch (e) {
        if ((e as any).response) {
          if ((e as any).response.status === 401) {
            // console.log("Authentication failed. Should now show sign-in dialog.");
          }
        } else {
          console.error(e);
          // No response. Api is not there?
          this.apiError = true;
          vuetifyToast.error("API connection error.");
        }
      }
    }

    // Pre-select first destination if not selected
    if (!(this.selectedDestinationId > 0) && this.destinations.length > 0) {
      const firstDestinationId = this.destinations[0].id;
      this.$router.replace({
        name: Routes.destinationRoute.name,
        params: {
          destinationId: firstDestinationId.toString(),
        },
      });
    }

    this.isResourcesLoading = false;
  }

    ellipsis(text: string, len: number): string {
        if (text && text.length > len) {
            text = text.substring( 0, len) + "...";
        }
        return text;
    }

  async getAllDestinations(): Promise<any> {
    // console.log("Getting all destinations ...")
    return this.$store.dispatch("destination/getAllDestinations");
  }

  async getAllOrganizations(): Promise<any> {
    return this.$store.dispatch("organization/getOrganizations");
  }

  async created() {
    (window as any).__appRoot = this;

    // Load l10n files
    await this.$store.dispatch("app/loadLocales");

    // Set locale
    if (!this.$i18n.locale()) {
      // console.log("Setting locale ...")
      // If current locale is not set, take it from:
      let locale;
      if (!locale) {
        // 1. Previously selected locale, stored in localstorage
        locale = this.$store.state.app.locale;
        // console.log("  Checking locale stored in localstorage:", locale);
      }
      if (!locale) {
        //  // 2. User browser's default locale, if supported
        locale = navigator.language;
        // console.log("  Checking default browser language:", locale);
        const dashIndex = locale.indexOf("-");
        if (dashIndex > -1) {
          locale = locale.substring(0, dashIndex);
          // console.log("  Two-letter browser language:", locale);
        }
      }
      if (
        !locale ||
        !this.$store.state.app.supportedLocaleCodes.includes(locale)
      ) {
        locale = this.$store.state.app.defaultLocale;
        // console.log("  Locale not supported. Setting locale to defaultLocale:", locale);
      }
      // Update the locale (on screen), don't save it to store
      // (user's own selection should be saved to store, not defaults)
      // console.log("  Setting locale:", locale);
      this.$i18n.set(locale);
    }

    (window as any)._approot = this;

    // console.log("Starting to poll API info ...")
    this.pollApiInfo();

    this.$root.$on(AppEvents.APP_LOGIN_SUCCESS, async () => {
      await this.initApp();
    });

    await this.initApp();
  }

  mounted() {
    this.themeToggleEnabled =
      document.location.host.indexOf("localhost") > -1 ||
      document.location.host.indexOf("staging") > -1 ||
      document.location.hash.indexOf("dev_theme_testing") > -1;

    this.toggleTranslateMode(this.translateMode);

    this.$root.$on(AppEvents.APP_API_UNAUTHORIZED, async () => {
      // console.log("AppRoot:", "APP_API_UNAUTHORIZED received");
      vuetifyToast.info(
        this.$i18n.translate(
          "generic.error.api_unauthorized",
          "Authentication failed. Your session might have expired. " +
            "Please sign in again."
        ),
        {
          timeout: 25000,
          dismissible: true,
        }
      );
    });
  }

  async getOrganizationPtpNames(organizationId: string) {
    await this.$store.dispatch(
      "organization/getPtpNamesForOrganization",
      {
        params: {
          id: organizationId,
        },
      }
    );
  }

  async getOrganizationCostCenters(organizationId) {
    await this.$store.dispatch(
      "organization/getCostCentersForOrganization",
      {
        params: {
          id: organizationId,
        },
      }
    );
  }

  async pollApiInfo() {
    try {
      const result = await this.$store.dispatch("app/getApiInfo");
      if (this.$store.state.app.apiInfo.frontend) {
        const apiInfoVersion: Version = new Version(
          this.$store.state.app.apiInfo.frontend.version
        );
        const currentVersion: Version = this.$store.state.app.version;
        this.newVersion = !currentVersion.isGreaterOrEqual(apiInfoVersion)
          ? apiInfoVersion.toString()
          : "";
      }
      // TODO: Set app to online? Here or in store?
    } catch (ex) {
      // TODO: Set app to offline? Here or in store?
    }
    this.pollApiInfoTimeout = setTimeout(
      this.pollApiInfo,
      this.$store.state.app.apiInfoPollingTimeoutMS
    );
  }

  onNewVersionOkClick() {
    (window as any).location.reload();
  }

  async onToggleWeekStartsOnSunday() {
    this.$store.dispatch(
      "user/setWeekStartsOnSunday",
      !this.weekStartsOnSunday
    );
  }

  async onRefreshClick() {
    this.refreshing = true;
    try {
      console.log("Getting all destinations ...");
      await this.$store.dispatch("destination/getAllDestinations");
      if (this.$store.state.route.name.includes("Arrival")) {
        console.log("Getting destination arrivalDepartures ...");
        await this.$store.dispatch(
          "arrivalDeparture/getDestinationArrivalDepartures",
          {
            params: {
              destinationId: this.routeDestinationId,
              refreshCache: true,
            },
          }
        );
      } else if (this.$store.state.route.name.includes("Timetable")) {
        console.log("Getting all timetables ...");
        await this.$store.dispatch("timetable/getAllTimetables");
        console.log("Getting all rides ...");
        await this.$store.dispatch("ride/getAllRides");
      } else if (this.$store.state.route.name.includes("Booking")) {
        console.log("Getting all bookings ...");
        this.$store.dispatch("booking/getDestinationBookings", {
          params: {
            destinationId: this.routeDestinationId,
          },
        });
      }
    } catch (e) {
      if ((e as any).response) {
        if ((e as any).response.status === 401) {
          //   console.log("Authentication failed. Should now show sign-in dialog.");
          this.$store.dispatch("auth/authLogout");
        }
      } else {
        console.error(e);
        vuetifyToast.error(
          this.$i18n.translate(
            "error.api_connection_error",
            "API connection error."
          )
        );
      }
    }
    this.refreshing = false;
  }

  onMenuContactSupportClick() {
    this.$router.push(Routes.contactSupportRoute);
    this.drawer = false;
  }

  onMenuSignOutClick() {
    this.$store.dispatch("auth/authLogout");
    this.drawer = false;
    this.$router.push(Routes.signInRoute);
  }
  onMenuSignInClick() {
    this.drawer = false;
    this.$router.push(Routes.signInRoute);
  }

  async onDestinationSelectChange(e) {
    // console.log("ArrivalsDeparturesPage", "onDestinationSelectChange()", "e:", e);
    (this.$refs.destinationSelectRef as any).blur();
  }

  async onDestinationSelectInput(e) {
    //   this.$router.replace({
    //     name: 'ArrivalsDeparturesDestination',
    //     params: {
    //       destinationId: this.selectedDestinationId.toString(),
    //     }
    //   });
  }

  async onPassengerSelect(passengerSearchResult: IPassengerSearchResult) {
    if (passengerSearchResult){
      await this.$store.dispatch("passenger/getPassengerById", {params: { id:  passengerSearchResult.id}});
    }
    if (passengerSearchResult.destinationId) {
      const destRole = this.me.destinationRoles.find(d => d.destinationId === passengerSearchResult.destinationId);
      if (destRole && destRole.passengerPermission >= PermissionEnum.Read) {
        this.$router.push({
          name: Routes.editPeopleRoute.name,
          params: { destinationId: passengerSearchResult.destinationId.toString(), passengerId: passengerSearchResult.id.toString() },
        });
      }
    } else {
      this.$router.push({
        name: Routes.editPassengerRoute.name,
        params: { passengerId: passengerSearchResult.id + "" },
      });
    }
  }

  onTimeTableSelect(timeTable: ITimetableSearchResult) {
    this.$router.push({
      name: Routes.timetableRoute.name,
      params: { destinationId: timeTable.destinationId.toString(), timetableId: timeTable.id }
    })
  }

  async onRetryClick() {
    this.apiError = false;
    await this.initApp();
  }
  onDrawerItemClick(drawerItem) {
    // console.log("onDrawerItemClick", drawerItem)
  }
  onResizeTranslationPane() {
    // console.log("onResizeSplitPane()")
    this.translatePanePreferredWidth =
      this.translatePaneHtmlElement.style.width;
  }
  beforeDestroy() {
    if (this.pollApiInfoTimeout) {
      clearTimeout(this.pollApiInfoTimeout);
    }
  }
}
